<template>
  <template v-if="activeAppId && isInStandardWebEnv()">
    <div
      data-test="top-menu-button"
      :class="['menu-title', { 'menu-selected': isShow }]"
      @click="($event) => clickMenuComponent()"
    >
      {{ activeItem?.name ?? '' }}
    </div>
    <Teleport to="body">
      <DialogContainer :show="isShow" @close="clickMenuComponent">
        <div class="menu-hidden">
          <div class="menu-hidden-header">
            <div class="menu-hidden-header-title">
              <img class="header-title-icon" src="@assets/svgs/icon-my-application.svg" />
              <span class="header-title-content">{{ $t('owork_base.my_applications') }}</span>
            </div>
          </div>
          <div class="menu-hidden-body">
            <template v-for="item in appMenuList" :key="item.name">
              <MainHeaderMenuAppItem
                :data-test="'menu-item-mapp-' + item.id"
                :is-selected="activeItem === item"
                :is-trusted-client="isTrustedClient"
                :item="item"
                @on-click="onAppMenuItemClicked"
              />
            </template>
            <template v-for="item in cloudMenuList" :key="item.name">
              <MainHeaderMenuCloudItem
                :data-test="item.needClientValidation ? 'menu-item-cloud' : 'menu-item-cloud-' + item.trackId"
                :item="item"
                :is-trusted-client="isTrustedClient"
                @on-click="clickMenuComponent"
              />
            </template>
          </div>
        </div>
      </DialogContainer>
    </Teleport>
  </template>
</template>

<script lang="ts" setup>
  import { onMounted, ref, shallowRef, watch, computed } from 'vue'
  import DialogContainer from '@components/dialog/DialogContainer.vue'
  import MainHeaderMenuAppItem from './MainHeaderMenuAppItem.vue'
  import MainHeaderMenuCloudItem from './MainHeaderMenuCloudItem.vue'
  import type { AppMenuInfo } from '@core/menus/MenuManager'
  import { INSTANCE as menuManager } from '@core/menus/MenuManager'
  import type { AppInfo, BaseAppInfo } from '@core/application/ApplicationManager'
  import Cookies from 'js-cookie'
  import { INSTANCE as ApplicationManager } from '@core/application/ApplicationManager'
  import { INSTANCE as UserInfoManager } from '@core/userInfo/UserInfoManager'
  import { useOtrack } from '@core/compositions/Otrack'
  import { menuAppChineseNameMap, TrackAppId, TrackEventMap } from '@core/TrackConstants'
  import { isEmpty } from 'lodash'
  import { useSharedActiveMenuItem } from '@core/compositions/ActiveMenuItem'
  import { onBeforeRouteUpdate } from 'vue-router'
  // 云服务APP icon标识
  import iconGallery from '@assets/svgs/icon-cloud-gallery.png'
  import iconGalleryLocked from '@assets/svgs/icon-cloud-gallery-locked.png'
  import iconMessages from '@assets/svgs/icon-cloud-messages.png'
  import iconMessagesLocked from '@assets/svgs/icon-cloud-messages-locked.png'
  import iconContacts from '@assets/svgs/icon-cloud-contacts.png'
  import iconContactsLocked from '@assets/svgs/icon-cloud-contacts-locked.png'
  import iconRecorder from '@assets/svgs/icon-cloud-recorder.png'
  import iconRecorderLocked from '@assets/svgs/icon-cloud-recorder-locked.png'
  import iconCloudDriver from '@assets/svgs/icon-cloud-driver.png'
  import iconCloudDriverLocked from '@assets/svgs/icon-cloud-driver-locked.png'
  import iconBrowser from '@assets/svgs/icon-cloud-browser.png'
  import iconBrowserLocked from '@assets/svgs/icon-cloud-browser-locked.png'
  import iconFindDevice from '@assets/svgs/icon-cloud-find-device.png'
  import iconFindDeviceLocked from '@assets/svgs/icon-cloud-find-device-locked.png'
  import iconHistory from '@assets/svgs/icon-cloud-history.png'
  import iconHistoryLocked from '@assets/svgs/icon-cloud-history-locked.png'
  import { MicroAppId } from '@/core/application/ApplicationConstants'
  import { useElectron } from '@/core/compositions/Electron'
  import { useI18n } from 'vue-i18n'
  import { useSharedScreen } from '@/core/compositions/Screen'

  const { isStandardScreen } = useSharedScreen()
  const { t } = useI18n()
  const isInStandardWebEnv = () => useElectron() === undefined
  // 云服务APP entry
  const ENTRY_URL_GALLERY = '/module.html#/photo'
  const ENTRY_URL_MESSAGES = '/module.html#/sms'
  const ENTRY_URL_CONTACTS = '/module.html#/contact'
  const ENTRY_URL_RECORDER = '/module.html#/audio'
  const ENTRY_URL_CLOUD = '/module.html#/clouddrive'
  const ENTRY_URL_BROWSER = '/module.html#/browser'
  const ENTRY_URL_FIND_DEVICE = '/pagemodule.html#/find'
  const ENTRY_URL_HISTORY = '/module.html#/olddata'

  const baseCloudMenuList: BaseAppInfo[] = [
    {
      name: t('owork_base.application_name.gallery'),
      entry: ENTRY_URL_GALLERY,
      icon: iconGallery,
      trackId: TrackAppId.GALLERY,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.gallery',
      iconLocked: iconGalleryLocked,
    },
    {
      name: t('owork_base.application_name.messages'),
      entry: ENTRY_URL_MESSAGES,
      icon: iconMessages,
      trackId: TrackAppId.MESSAGES,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.messages',
      iconLocked: iconMessagesLocked,
    },
    {
      name: t('owork_base.application_name.contacts'),
      entry: ENTRY_URL_CONTACTS,
      icon: iconContacts,
      trackId: TrackAppId.CONTACTS,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.contacts',
      iconLocked: iconContactsLocked,
    },
    {
      name: t('owork_base.application_name.recorder'),
      entry: ENTRY_URL_RECORDER,
      icon: iconRecorder,
      trackId: TrackAppId.RECORDER,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.recorder',
      iconLocked: iconRecorderLocked,
    },
    {
      name: t('owork_base.application_name.cloud_storage'),
      entry: ENTRY_URL_CLOUD,
      icon: iconCloudDriver,
      trackId: TrackAppId.CLOUD_STORAGE,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.cloud_storage',
      iconLocked: iconCloudDriverLocked,
    },
    {
      name: t('owork_base.application_name.browser'),
      entry: ENTRY_URL_BROWSER,
      icon: iconBrowser,
      trackId: TrackAppId.BROWSER,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.browser',
      iconLocked: iconBrowserLocked,
    },
    {
      name: t('owork_base.application_name.find_device'),
      entry: ENTRY_URL_FIND_DEVICE,
      icon: iconFindDevice,
      trackId: TrackAppId.FIND_DEVICE,
      needClientValidation: false,
      internationalizationKey: 'owork_base.application_name.find_device',
      iconLocked: iconFindDeviceLocked,
    },
    {
      name: t('owork_base.application_name.history'),
      entry: ENTRY_URL_HISTORY,
      icon: iconHistory,
      trackId: TrackAppId.HISTORY,
      needClientValidation: true,
      internationalizationKey: 'owork_base.application_name.history',
      iconLocked: iconHistoryLocked,
    },
  ].map(
    (app) =>
      new Proxy(app, {
        get(target: BaseAppInfo, property: keyof BaseAppInfo): BaseAppInfo[keyof BaseAppInfo] {
          if (property === 'name' && target.internationalizationKey) {
            return t(target.internationalizationKey)
          }

          return target[property]
        },
      })
  )

  const COOKIE_KEY_HAS_HISTORY_APP = 'HAS_HISTORY_DATA'
  const noHistoryApp = !Cookies.get(COOKIE_KEY_HAS_HISTORY_APP) || Cookies.get(COOKIE_KEY_HAS_HISTORY_APP) === 'false'
  const cloudMenuList = computed(() =>
    noHistoryApp ? baseCloudMenuList.filter((e) => !(noHistoryApp && e.entry === ENTRY_URL_HISTORY)) : baseCloudMenuList
  )

  const { activeItem, activeAppId, setAppMenuList, updateCurrentAppId, currentAppTrackName } = useSharedActiveMenuItem()
  const isShow = ref<boolean>(false)
  const appMenuList = shallowRef<AppInfo[]>([])
  const isTrustedClient = ref<boolean>(false)
  const { track } = useOtrack()

  const callTrack = (clickedApp: BaseAppInfo) => {
    if (clickedApp.trackId === activeItem.value?.trackId) return
    const clicked_app_name = clickedApp.trackId ? menuAppChineseNameMap.get(clickedApp.trackId) : ''

    if (isEmpty(currentAppTrackName.value) || isEmpty(clicked_app_name)) return

    track(
      {
        eventId: TrackEventMap.SWITCHING_APPLICATION,
      },
      {
        current_app_name: currentAppTrackName.value,
        clicked_app_name,
      }
    )
  }

  const clickMenuComponent = (app?: BaseAppInfo) => {
    if (app) {
      callTrack(app)
    }

    isShow.value = !isShow.value
  }

  const onAppMenuItemClicked = (app: AppInfo) => {
    isShow.value = false
    callTrack(app)
  }

  watch(
    () => isShow.value,
    async () => {
      if (isShow.value) {
        isTrustedClient.value = await UserInfoManager.isTrustedClient()
      }
    }
  )

  watch(isStandardScreen, (newValue) => {
    if (!newValue) {
      isShow.value = false
    }
  })

  const OCLOUD_TRANSFER_STATION = 'OCLOUD_TRANSFER_STATION'

  const isShowTransferDockEntry = () => Cookies.get(OCLOUD_TRANSFER_STATION) === 'true'

  const initMenu = () => {
    menuManager.init(ApplicationManager.getAppList())
    const categoryMenuList: AppMenuInfo[] = menuManager.getMenuList()

    appMenuList.value = categoryMenuList
      .filter((item) => {
        if (item.appMenuList[0]?.id === MicroAppId.TRANSFER_DOCK) return isShowTransferDockEntry()

        return item.appMenuList.length !== 0
      })
      .map((item) => item.appMenuList[0])

    setAppMenuList(appMenuList.value, categoryMenuList)
    // TODO: application manager use getDefaultAppId for this propose, this logic conflicts with it
  }

  onBeforeRouteUpdate((updateGuard) => {
    // 监听路由变化
    updateCurrentAppId(updateGuard.path)
  })

  onMounted(async () => {
    initMenu()

    updateCurrentAppId()

    isTrustedClient.value = await UserInfoManager.isTrustedClient()
  })
</script>

<style lang="stylus" scoped>

  $HOVER_BACKGROUND_COLOR = var(---couiColorPress, rgba(0, 0, 0, 0.12))
  .menu-selected
    background-color rgba(84,86,104,.06)
    &::after
      triangle-transform()
  .menu-title
    flex-align-center()
    font-size 16px
    color #000
    font-weight default_font_weight // 设置为500和云服务对齐，避免跳转时有明显变化
    cursor pointer
    height 44px
    line-height 44px
    padding 0 6px
    border-radius 10px
    ellipsis()
    &::after
      image-triangle-after()
      margin-left 8px
  .menu-hidden
    position absolute
    top 49px
    left 138px
    width 336px
    padding 0 12px 4px
    background-color #fff
    box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.08)
    border-radius 12px
    z-index $zIndexTop
    .menu-hidden-body
      flex-align-center()
      flex-wrap wrap
    .menu-hidden-header
      width 100%
      height 59px
      .menu-hidden-header-title
        position absolute
        left 24px
        top 23px
        display flex
        align-items center
        .header-title-icon
          margin-right 6px
          rect(16px)
          margin-top -1px
        .header-title-content
          font-size 16px
          font-weight default_font_weight
          color var(--color-label-coui-color-label-primary, rgba(0, 0, 0, 0.9))
    .menu-item
      rect(72px,96px)
      margin-right 8px
      margin-bottom 8px
      border-radius 12px
      cursor pointer
      display flex
      flex-direction column
      justify-content center
      align-items center
      &:hover
        background-color $HOVER_BACKGROUND_COLOR  !important
      &:nth-child(4n)
        margin-right 0 !important
      &.select-item
        background-color $HOVER_BACKGROUND_COLOR
  @import '../styles/largeScreenAdaptive/MainHeaderMenu.styl'
</style>
